import { useApolloClient, useQuery } from '@apollo/client';
import { noop } from 'lodash';
import { useEffect } from 'react';

import type { MyProfileQueryData } from '@skippay/core/@types/myProfile';
import useInitialCountryLocale from '@skippay/core/hooks/useInitialCountryLocale';
import useUserCountryRelatedInfo from '@skippay/core/hooks/useUserCountryRelatedInfo';
import { nonNullable } from '@skippay/core/utils/nonNullable';

import type { UpsellServiceAvailableOffer } from '@types/upsellServiceAvailableOffer';
import { useFeatureIsEnabled } from 'components/IfFeatureEnabled/useFeatureIsEnabled';
import MyProfileQuery from 'queries/MyProfileQuery.graphql';

import { jarabotDevStore } from './devStore';
import { useJarabotFeatureIsEnabled } from './featureFlag';
import { readInsuranceOfferMemory, saveInsuranceOfferContact } from './insuranceOffer/memory';
import { createInsuranceOfferPayload } from './insuranceOffer/payload';
import { selectInsuranceContact } from './insuranceOffer/selection';
import {
	JARABOT_DELAYS,
	JARABOT_EVENTS,
	JARABOT_STORAGE_KEYS,
	jarabotClient,
} from './jarabotClient';
import { useJarabotTracking } from './JarabotTrackingProvider';
import MyUpsellServicesAvailableOffersQuery from './queries/MyUpsellServicesAvailableOffersQuery.graphql';
import { useJarabotWidgetReady } from './useJarabotWidgetReady';
import { getFlag, getJarabotDelay, setFlag } from './utils';

const DELAY = getJarabotDelay(
	JARABOT_STORAGE_KEYS.INSURANCE_OFFER_DELAY,
	JARABOT_DELAYS.INSURANCE_OFFER
);
const SESSION_KEY_PREFIX = 'jarabot_insurance_offer_';
const EVENT = JARABOT_EVENTS.CUSTOMER_INSURANCE_OFFER;
// The offer is evaluated app-wide rather than from a single screen, so it reports its own surface.
const SCREEN_NAME = 'insurance_offer';

type MyUpsellServicesAvailableOffersData = {
	myUpsellServicesAvailableOffers: Array<UpsellServiceAvailableOffer | null> | null;
};

export function useJarabotInsuranceOffer(): void {
	const client = useApolloClient();
	const isJarabotEnabled = useJarabotFeatureIsEnabled();
	const { isFeatureEnabled: isUpsellServicesEnabled } = useFeatureIsEnabled('upsellServices');
	const canEvaluate = isJarabotEnabled && isUpsellServicesEnabled;
	const { country } = useInitialCountryLocale();
	const { selfCareUrl } = useUserCountryRelatedInfo(country);
	const isWidgetReady = useJarabotWidgetReady();
	const { trackJarabotDropoff } = useJarabotTracking();

	const { data: profileData } = useQuery<MyProfileQueryData>(MyProfileQuery, {
		fetchPolicy: 'cache-first',
		skip: !canEvaluate,
	});
	const customerId = profileData?.myProfile?.id;

	useEffect(() => {
		if (!canEvaluate || country !== 'CZ' || !isWidgetReady || !customerId) return undefined;

		const sessionKey = `${SESSION_KEY_PREFIX}${customerId}`;
		if (getFlag(sessionKey)) return undefined;

		let isCancelled = false;

		const timeout = setTimeout(() => {
			setFlag(sessionKey);

			client
				.query<MyUpsellServicesAvailableOffersData>({
					query: MyUpsellServicesAvailableOffersQuery,
					fetchPolicy: 'no-cache',
				})
				.then(({ data }) => {
					if (isCancelled) return;

					const offers = (data?.myUpsellServicesAvailableOffers ?? []).filter(nonNullable);
					const now = new Date();
					const memory = readInsuranceOfferMemory(customerId, now);
					jarabotDevStore.trackInsuranceMemory(memory);

					const selection = selectInsuranceContact(offers, memory, now);

					if (!selection) return;

					const offerIds = selection.purchases.map((purchase) => purchase.id);

					jarabotClient.sendMessage(
						EVENT,
						createInsuranceOfferPayload({ selection, selfCareUrl }),
						true,
						{ shake: true }
					);
					trackJarabotDropoff(EVENT, SCREEN_NAME);

					const saved = saveInsuranceOfferContact({
						memory,
						customerId,
						offerIds,
						contactType: selection.contactType,
						now,
					});

					jarabotDevStore.trackInsuranceSent(selection.contactType, offerIds);
					jarabotDevStore.trackInsuranceMemory(saved);
				})
				.catch(noop);
		}, DELAY);

		return () => {
			isCancelled = true;
			clearTimeout(timeout);
		};
	}, [canEvaluate, country, isWidgetReady, customerId, selfCareUrl, client, trackJarabotDropoff]);
}
